import { initCarouselSwipe } from '../../utils/carousel-swipe';
import { equalizeHeights } from '../../utils/equalize-heights';

const ROOT_SELECTOR = '[data-carousel]';
const VIEWPORT_SELECTOR = '[data-carousel-viewport]';
const TRACK_SELECTOR = '[data-carousel-track]';
const SLIDE_SELECTOR = '[data-carousel-slide]';
const PROGRESS_SELECTOR = '[data-carousel-progress]';
const PREV_SELECTOR = '[data-carousel-prev]';
const NEXT_SELECTOR = '[data-carousel-next]';
const BLADE_SELECTOR = '[data-carousel-blade]';
const BLADE_OPEN_SELECTOR = '[data-carousel-blade-open]';
const BLADE_CLOSE_SELECTOR = '[data-carousel-blade-close]';
const BLADE_VIEW_SELECTOR = '[data-carousel-blade-view]';
const HEADER_SELECTOR = '.carousel__card-header';

function getVisibleCount(viewport: HTMLElement, slideWidth: number, gap: number): number {
  if (!slideWidth) return 1;
  return Math.max(1, Math.floor((viewport.offsetWidth + gap) / (slideWidth + gap)));
}

export function initCarousel(): void {
  document.querySelectorAll<HTMLElement>(ROOT_SELECTOR).forEach(initCarouselRoot);
}

function initCarouselRoot(root: HTMLElement): void {
  initCarouselTrack(root);
  initCarouselBlade(root);
}

function initCarouselBlade(root: HTMLElement): void {
  const blade = root.querySelector<HTMLElement>(BLADE_SELECTOR);
  if (!blade) return;

  const views = blade.querySelectorAll<HTMLElement>(BLADE_VIEW_SELECTOR);
  const openButtons = root.querySelectorAll<HTMLButtonElement>(BLADE_OPEN_SELECTOR);
  const closeButtons = blade.querySelectorAll<HTMLButtonElement>(BLADE_CLOSE_SELECTOR);

  function setBladeOpen(isOpen: boolean, viewIndex?: string): void {
    document.body.classList.toggle('carousel-blade-open', isOpen);
    blade.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

    if (isOpen && viewIndex !== undefined) {
      views.forEach((view) => {
        const isActive = view.getAttribute('data-carousel-blade-view') === viewIndex;
        view.classList.toggle('carousel__blade-view--hidden', !isActive);
      });
    }
  }

  openButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const index = button.getAttribute('data-carousel-blade-open');
      if (index === null) return;
      setBladeOpen(true, index);
    });
  });

  closeButtons.forEach((button) => {
    button.addEventListener('click', () => setBladeOpen(false));
  });

  window.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.body.classList.contains('carousel-blade-open')) {
      setBladeOpen(false);
    }
  });
}

function initCarouselTrack(root: HTMLElement): void {
  const viewport = root.querySelector<HTMLElement>(VIEWPORT_SELECTOR);
  const track = root.querySelector<HTMLElement>(TRACK_SELECTOR);
  const slides = root.querySelectorAll<HTMLElement>(SLIDE_SELECTOR);
  const progress = root.querySelector<HTMLElement>(PROGRESS_SELECTOR);
  const prev = root.querySelector<HTMLButtonElement>(PREV_SELECTOR);
  const next = root.querySelector<HTMLButtonElement>(NEXT_SELECTOR);

  if (!viewport || !track || !slides.length) return;

  let index = 0;

  function equalizeCardHeaders(): void {
    equalizeHeights(root.querySelectorAll<HTMLElement>(HEADER_SELECTOR));
  }

  function getBaseOffset(): number {
    const slide = slides[0];
    const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
    return index * (slide.offsetWidth + gap);
  }

  function applyTrackOffset(offsetPx: number): void {
    track.style.transform = `translateX(-${offsetPx}px)`;
  }

  function update(): void {
    const slide = slides[0];
    const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
    const slideWidth = slide.offsetWidth;
    const visible = getVisibleCount(viewport, slideWidth, gap);
    const maxIndex = Math.max(0, slides.length - visible);

    index = Math.min(index, maxIndex);
    applyTrackOffset(getBaseOffset());

    if (progress) {
      const progressEnd = Math.min(slides.length, index + visible);
      progress.style.width = `${(progressEnd / slides.length) * 100}%`;
    }

    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= maxIndex;

    equalizeCardHeaders();
  }

  function goTo(newIndex: number): void {
    const slide = slides[0];
    const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
    const visible = getVisibleCount(viewport, slide.offsetWidth, gap);
    const maxIndex = Math.max(0, slides.length - visible);

    index = Math.max(0, Math.min(maxIndex, newIndex));
    update();
  }

  prev?.addEventListener('click', () => goTo(index - 1));
  next?.addEventListener('click', () => goTo(index + 1));
  window.addEventListener('resize', update);

  if (typeof window.ResizeObserver !== 'undefined') {
    const resizeObserver = new window.ResizeObserver(() => equalizeCardHeaders());
    resizeObserver.observe(track);
  }

  slides.forEach((slide) => {
    slide.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', () => equalizeCardHeaders(), { once: true });
      }
    });
  });

  initCarouselSwipe({
    viewport,
    getBaseOffset,
    onDrag: applyTrackOffset,
    onCommit: (direction) => {
      if (direction === 0) {
        update();
        return;
      }

      goTo(index + direction);
    },
  });

  update();
}
